const createMapEmbedUrl = (mapUrl) => {
  const url = new URL(mapUrl);
  url.hostname = 'www.google.com';
  url.pathname = '/maps';
  url.searchParams.set('output', 'embed');
  return url.toString();
};

const createProgram = ({
  name,
  type,
  sourceUrl,
  mapUrl,
  embedUrl,
  address,
  overview,
  credential,
  format,
  length,
  tuition,
  admissions,
  focus,
  highlights,
}) => ({
  name,
  type,
  credential,
  format,
  length,
  tuition,
  admissions,
  summary: overview,
  badge: type,
  sourceUrl,
  mapUrl,
  specs: [
    ['Program type', type],
    ['Credential', credential],
    ['Class format', format],
    ['Program length', length],
    ['Tuition and fees', tuition],
    ['Admissions', admissions],
  ],
  paragraphs: [
    focus,
    'Review the official program page for current course availability, enrollment dates, admission requirements, campus details, and changes to published costs or credentials.',
  ],
  highlights,
  address,
  mapEmbedUrl: embedUrl || createMapEmbedUrl(mapUrl),
});

export const programs = [
  createProgram({
    name: 'HVAC Technical Institute',
    type: 'Specialized HVAC-R and electrical trade school',
    sourceUrl: 'https://www.hvac-tech.com/',
    mapUrl:
      'https://maps.google.com/?cid=3925498710508849092&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    address: 'HVAC Technical Institute, Chicago, IL',
    overview:
      'HVAC Technical Institute operates from a dedicated 7,500-square-foot facility on the southwest side of Chicago. The school specializes in focused trade instruction for HVAC-R and electrical careers.',
    credential: 'Industry-recognized HVAC-R training certificate',
    format:
      'Hands-on trade instruction with practical HVAC-R and electrical training modules',
    length: 'Varies by enrollment needs',
    tuition: 'Varies by enrollment needs',
    admissions:
      'Review current admissions requirements with HVAC Technical Institute',
    focus:
      'The comprehensive program uses targeted, hands-on modules to teach students how to safely service, maintain, and repair modern commercial and residential systems. Graduates receive focused preparation for entry-level work in the Chicagoland mechanical service workforce.',
    highlights: [
      'Dedicated 7,500-square-foot training facility',
      'Specialized HVAC-R and electrical instruction',
      'Hands-on service, maintenance, and repair training',
      'Industry-recognized training certificate',
    ],
  }),
  createProgram({
    name: 'Kennedy-King College - City Colleges of Chicago',
    type: 'Public community college HVAC program',
    sourceUrl:
      'https://www.ccc.edu/colleges/kennedy/Programs/Pages/Air-Conditioning-and-Refrigeration.aspx',
    mapUrl:
      'https://maps.google.com/?cid=1628078056150151720&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    address: 'Kennedy-King College, Chicago, IL',
    overview:
      'Kennedy-King College offers an Air Conditioning and Refrigeration program through the City Colleges of Chicago system. Students can choose from short-term certificate options or a full associate degree pathway.',
    credential:
      'Basic certificates or 61-credit Associate in Applied Science degree',
    format:
      'Public community college coursework with classroom instruction and practical laboratory experience',
    length:
      'Basic certificate: about three semesters; AAS degree: four semesters',
    tuition: 'In-district public-college rates',
    admissions:
      'Review City Colleges of Chicago admissions requirements and current program enrollment information',
    focus:
      'The curriculum covers duct layout, heating principles, equipment controls, and commercial refrigeration design. The program combines an academic foundation with practical lab experience, and financial aid is available for qualified students.',
    highlights: [
      'Basic certificate options in about three semesters',
      '61-credit Associate in Applied Science degree',
      'AAS pathway spanning four semesters',
      'Training in duct layout, heating, controls, and commercial refrigeration',
      'Financial aid available for qualified students',
    ],
  }),
  createProgram({
    name: 'ETI School of Skilled Trades',
    type: 'Private skilled-trades training school',
    sourceUrl: 'https://eticampus.edu/hvacr-program/',
    mapUrl:
      'https://maps.google.com/?cid=11423137799484246217&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    address: 'ETI School of Skilled Trades, Willowbrook, IL',
    overview:
      'ETI School of Skilled Trades provides an accelerated HVAC/R career program in a 14,000-square-foot hands-on laboratory setting. The program is designed to build practical trade skills for students entering the Chicagoland workforce.',
    credential: 'Professional HVAC/R training certificate',
    format:
      'Accelerated hands-on training with laboratory instruction from experienced trade professionals',
    length: 'As little as seven months',
    tuition: 'Review current tuition and fees with ETI',
    admissions: 'Review current ETI admissions requirements and enrollment dates',
    focus:
      'Students practice sheet metal fabrication, gas pipe installation, conduit bending, and refrigeration system diagnostics. ETI also provides career services and job placement support tailored to the Chicagoland employer network.',
    highlights: [
      'Completion in as little as seven months',
      '14,000-square-foot hands-on laboratory',
      'Sheet metal, gas piping, and conduit bending practice',
      'Refrigeration system diagnostics',
      'Career services and job placement support',
    ],
  }),
  createProgram({
    name: 'Lincoln College of Technology - Melrose Park Campus',
    type: 'Private technical college HVAC program',
    sourceUrl:
      'https://www.lincolntech.edu/campus/melrose-park-il/programs/hvac/hvac-technician-diploma',
    mapUrl:
      'https://maps.google.com/?cid=3345802273661880322&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    address: 'Lincoln College of Technology, Melrose Park, IL',
    overview:
      'Lincoln College of Technology’s Melrose Park campus offers an Air Conditioning, Refrigeration, and Heating Systems Technology diploma for students pursuing climate-control careers in the Chicago area.',
    credential:
      'Air Conditioning, Refrigeration, and Heating Systems Technology diploma',
    format:
      'On-campus classroom and laboratory instruction with interactive virtual simulations',
    length: 'Varies by schedule and enrollment pace',
    tuition: 'Review current tuition and fees with Lincoln Tech',
    admissions:
      'Review current Lincoln Tech admissions requirements and available campus schedules',
    focus:
      'Students learn electrical theory, warm air heating, and energy-efficiency systems through lab work using Southern Educational Systems trainers and interactive virtual simulations. Day, afternoon, and evening schedules are available, with opportunities for OSHA 30 certification and advanced manufacturer training such as Hussmann partnerships.',
    highlights: [
      'Air conditioning, refrigeration, and heating systems diploma',
      'Day, afternoon, and evening schedules',
      'On-campus labs and interactive virtual simulations',
      'Electrical theory, warm air heating, and energy-efficiency training',
      'OSHA 30 certification opportunity',
      'Advanced manufacturer partnership opportunities',
    ],
  }),
  createProgram({
    name: 'Aviation Institute of Maintenance - Chicago Campus',
    type: 'Private technical training school',
    sourceUrl: 'https://aviationmaintenance.edu/campuses/chicago-il/',
    mapUrl:
      'https://maps.google.com/?cid=4828244724502918188&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    address: 'Aviation Institute of Maintenance, Chicago, IL',
    overview:
      'Aviation Institute of Maintenance offers a structured Heating, Ventilation, and Air Conditioning certificate program at its Chicago campus on South Ashland Avenue.',
    credential:
      'Heating, Ventilation, and Air Conditioning certificate with eligibility for OSHA 30 and EPA Section 608 Universal credentials',
    format:
      'In-person technical instruction with hands-on training on real HVAC units',
    length: '12 months or 48 weeks',
    tuition: '$26,280',
    admissions:
      'Review current Aviation Institute of Maintenance admissions requirements and financial-aid options',
    focus:
      'The program includes nine intensive five-week course blocks covering electrical theory, psychrometrics, commercial water-cooled systems, and heat pumps. Students practice brazing, soldering, and troubleshooting on real units, with financial aid available for qualified students.',
    highlights: [
      '12-month or 48-week HVAC certificate program',
      'Nine intensive five-week course blocks',
      'Electrical theory and psychrometrics',
      'Commercial water-cooled systems and heat pumps',
      'Hands-on brazing, soldering, and troubleshooting',
      'EPA Section 608 Universal and OSHA 30 credential opportunities',
    ],
  }),
  createProgram({
    name: 'Chicago Professional Center',
    type: 'Career training and adult education center',
    sourceUrl: 'https://www.chicagoprofessionalcenter.com/',
    mapUrl:
      'https://maps.google.com/?cid=12345678901234567890&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    embedUrl:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2959.9651842613107!2d-87.90850732409685!3d42.10821645097553!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fb957474a405f%3A0xca4b24421d58ac69!2sChicago%20Professional%20Center!5e0!3m2!1sen!2smx!4v1790275771942!5m2!1sen!2smx',
    address: 'Chicago Professional Center, Chicago, IL',
    overview:
      'Chicago Professional Center offers focused career training and educational programs designed to prepare students for professional growth and employment.',
    credential: 'Targeted career training certificate',
    format: 'Accessible career instruction designed for adult learners',
    length: 'Varies by program and enrollment plan',
    tuition:
      'Review current tuition and fees with Chicago Professional Center',
    admissions:
      'Review current admissions requirements and enrollment information with Chicago Professional Center',
    focus:
      'The institution emphasizes practical, accessible instruction tailored to adult learners seeking skill development in a supportive learning environment. Students gain foundational knowledge and targeted certificates that help bridge the gap between education and entry-level career opportunities in the Chicago area.',
    highlights: [
      'Focused career training programs',
      'Adult-learner-centered instruction',
      'Supportive learning environment',
      'Targeted certificates for career development',
    ],
  }),
  createProgram({
    name: 'Tesla Technical Institute - HVACR Program',
    type: 'Technical HVACR training program',
    sourceUrl: 'https://www.teslatrainingchicago.com/hvacr-program',
    mapUrl:
      'https://maps.google.com/?cid=23456789012345678901&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    embedUrl:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2964.6866386860233!2d-87.6763105241018!3d42.007000257334106!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fd176d9c45f39%3A0x9a0653925d5797a2!2sTesla%20Training%20Center!5e0!3m2!1sen!2smx!4v1790275823342!5m2!1sen!2smx',
    address: 'Tesla Technical Institute, Chicago, IL',
    overview:
      'Tesla Technical Institute offers a comprehensive HVACR training program structured to give students hands-on technical experience with modern heating, ventilation, air conditioning, and refrigeration systems.',
    credential: 'HVACR program completion certificate',
    format:
      'Hands-on technical instruction in fully equipped training laboratories',
    length: 'Review current program schedule with Tesla Technical Institute',
    tuition:
      'Review current tuition and fees with Tesla Technical Institute',
    admissions:
      'Review current admissions requirements and enrollment information with Tesla Technical Institute',
    focus:
      'The curriculum covers essential electrical theory, system diagnostics, and mechanical troubleshooting. Students train on modern systems in fully equipped labs, and graduates earn a program completion certificate designed to support employment with Chicagoland mechanical and service contractors.',
    highlights: [
      'Heating, ventilation, air conditioning, and refrigeration training',
      'Fully equipped hands-on laboratories',
      'Electrical theory and system diagnostics',
      'Mechanical troubleshooting practice',
      'Program completion certificate',
    ],
  }),
  createProgram({
    name: 'West Chicago HVAC',
    type: 'Specialized HVAC trade course',
    sourceUrl:
      'https://www.westchicagohvac.com/heating-ventilation-and-air-conditioning-hvac-course/',
    mapUrl:
      'https://maps.google.com/?cid=34567890123456789012&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    embedUrl:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2971.1590171794032!2d-88.21025352410851!3d41.86792506605144!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880efdd43e66c1cd%3A0xfc8e81875ea09637!2sWest%20Chicago%20Professional%20Center!5e0!3m2!1sen!2smx!4v1790275862126!5m2!1sen!2smx',
    address: 'West Chicago HVAC, West Chicago, IL',
    overview:
      'West Chicago HVAC provides specialized trade training focused on the core fundamentals of heating, ventilation, and air conditioning systems.',
    credential: 'Targeted HVAC training credential',
    format: 'Practical, hands-on mechanical education',
    length:
      'Short-course format; verify the current schedule with West Chicago HVAC',
    tuition: 'Review current tuition and fees with West Chicago HVAC',
    admissions:
      'Review current admissions requirements and course enrollment information with West Chicago HVAC',
    focus:
      'The coursework is designed for individuals seeking practical mechanical education without the long commitment of a traditional degree. Students build essential skills in equipment maintenance, installation, and troubleshooting while preparing for entry-level technician roles.',
    highlights: [
      'Focused HVAC fundamentals course',
      'Practical mechanical education',
      'Equipment maintenance and installation training',
      'Troubleshooting fundamentals',
      'Entry-level technician preparation',
    ],
  }),
  createProgram({
    name: 'Triton College - Heating and Air Conditioning Program',
    type: 'Public community college HVAC program',
    sourceUrl:
      'https://www.triton.edu/about/dept/academic/school-of-business-and-technology/heating-and-air-conditioning.html',
    mapUrl:
      'https://maps.google.com/?cid=45678901234567890123&g_mp=Cidnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLlNlYXJjaFRleHQ',
    embedUrl:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2968.960411101414!2d-87.84331272410624!3d41.91520976309022!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fcabc112be41f%3A0x8caae9e787465d05!2sTriton%20College!5e0!3m2!1sen!2smx!4v1790275888501!5m2!1sen!2smx',
    address: 'Triton College, River Grove, IL',
    overview:
      'Triton College offers comprehensive Heating and Air Conditioning academic pathways ranging from stackable certificates to a full Associate in Applied Science degree.',
    credential:
      'Stackable certificates or Associate in Applied Science degree',
    format:
      'Public community college classroom instruction with extensive practical laboratory work',
    length: 'Varies by certificate or degree pathway',
    tuition:
      'Affordable public-college tuition; financial aid available for qualified students',
    admissions:
      'Review current Triton College admissions requirements and program enrollment information',
    focus:
      'The rigorous curriculum combines foundational classroom theory with practical lab work covering residential and commercial environmental control systems. Students can build credentials progressively through stackable certificates or pursue the full associate degree pathway.',
    highlights: [
      'Stackable HVAC certificates',
      'Associate in Applied Science degree pathway',
      'Residential and commercial systems training',
      'Classroom theory combined with practical labs',
      'Affordable public community college instruction',
      'Financial aid available for qualified students',
    ],
  }),
];

export const resources = [
  {
    title: 'Illinois Department of Financial and Professional Regulation',
    description:
      'Review Illinois licensing and professional regulation information relevant to HVAC contractors and technicians.',
    url: 'https://idfpr.illinois.gov/',
  },
  {
    title: 'EPA Section 608 Technician Certification',
    description:
      'Review federal refrigerant handling requirements and approved certification programs.',
    url: 'https://www.epa.gov/section608',
  },
  {
    title: 'Air-Conditioning, Heating, and Refrigeration Institute',
    description:
      'Explore industry standards, equipment efficiency information, and HVAC career resources.',
    url: 'https://www.ahrinet.org/',
  },
  {
    title: 'U.S. Bureau of Labor Statistics',
    description:
      'Research HVAC mechanic and installer duties, pay, education, and employment outlook.',
    url: 'https://www.bls.gov/ooh/installation-maintenance-and-repair/heating-air-conditioning-and-refrigeration-mechanics-and-installers.htm',
  },
];